import { useEffect, useRef } from "react";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { useAudio } from "../context/AudioContext";
import { useTheme } from "../context/ThemeContext";

/**
 * PrefsSync — makes settings follow the ACCOUNT, not the browser.
 *
 * On sign-in: pulls the saved prefs (HD voice, reading speed, theme,
 * language) from /api/me/prefs and applies any the server knows about.
 * While signed in: pushes changes back (debounced) so the next login —
 * on ANY device — restores exactly what the user left behind.
 * Renders nothing.
 */
export default function PrefsSync() {
  const { user } = useAuth();
  const { hdVoice, setHdVoice, rate, setRate, language, setLanguage } = useAudio();
  const { themeId, setThemeId } = useTheme();
  const loadedForRef = useRef(null);
  const saveTimerRef = useRef(null);

  // Restore once per signed-in account.
  useEffect(() => {
    if (!user || user === false) {
      loadedForRef.current = null;
      return;
    }
    const key = user.id || user.email;
    if (loadedForRef.current === key) return;
    loadedForRef.current = key;
    api.get("/me/prefs").then(({ data }) => {
      const p = data?.prefs || {};
      // Server value wins when present; otherwise the local pick stays
      // and gets uploaded by the save effect below.
      if (p.hd_voice) setHdVoice(p.hd_voice);
      if (p.rate) setRate(Number(p.rate));
      if (p.theme) setThemeId(p.theme);
      if (p.language) setLanguage(p.language);
    }).catch(() => {});
  }, [user]); // eslint-disable-line

  // Save on change (debounced) — only after the restore ran.
  useEffect(() => {
    if (!user || user === false) return;
    const key = user.id || user.email;
    if (loadedForRef.current !== key) return;
    clearTimeout(saveTimerRef.current);
    saveTimerRef.current = setTimeout(() => {
      api.put("/me/prefs", {
        hd_voice: hdVoice ?? null,
        rate,
        theme: themeId,
        language,
      }).catch(() => {});
    }, 800);
    return () => clearTimeout(saveTimerRef.current);
  }, [user, hdVoice, rate, themeId, language]); // eslint-disable-line

  return null;
}
